/**
 * Coupons page.
 *
 * @module app/pages/CouponsPAge
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import EventViewAdapter from '../views/EventViewAdapter';
import jQuery from 'jquery';
import MarketMenuAdapter from '../components/MarketMenuAdapter';
import PageCommon from '../pages/PageCommon';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';

/**
 * Internal state flag used to determine whether page has been rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Pagination
 *
 * @type {Object}
 */
var pagination = {

    /**
     * Base link
     *
     * @type {String}
     */
    baseLink: null,

    /**
     * Collection of pointers with first/last sub/group IDs
     *
     * @type {Array}
     */
    pointers: [],

    /**
     * Pointers for first/last group/subgroup
     *
     * @type {Object}
     */
    currentPointers: null,

    /**
     * XMLHttpRequest
     *
     * @type {XMLHttpRequest}
     */
    request: null,

    /**
     * Row IDs
     *
     * @type {Array}
     */
    rowIds: [],

    /**
     * Lazy Loading initialize
     *
     * @returns {void}
     *
     * @private
     */
    init: function () {

        if (window.WH.sportsbook.totalPages > 1) {

            pagination.initializePagination();

            var config = pagination.getConfigByType(window.WH.sportsbook.sortOrder);

            // init lazy loading
            BasePage.LazyLoadingViewAdapter.init({
                totalPages: window.WH.sportsbook.totalPages,
                container: config.container,
                group: config.group,
                subGroup: config.subGroup,
                row: config.row,
                selection: config.selection,
                selectionOdds: config.selectionOdds,
                replaceGroupStr: config.replaceGroupStr,
                replaceSubGroupStr: config.replaceSubGroupStr,
                totalItemsPerPage: window.WH.sportsbook.eventsPerPage,
                pagination: pagination,
                highlightedSelections: SelectionViewAdapter.highlightedSelections,
                timeSelector: '.localisable',
                totalRowsCount: window.WH.sportsbook.totalRowsCount,
            });
        }
    },

    reset: function () {

        BasePage.LazyLoadingViewAdapter.destroy();
        pagination.init();
    },

    /**
     * Gets config details for date or competition grouping
     *
     * @param   {String}  type
     *
     * @returns {Object}
     */
    getConfigByType: function (type) {

        var config = {

            date: {
                container: '.event-container',
                group: '.market-group-wrapper',
                subGroup: '.mg-wrapper, .btmarket__wrapper',
                row: '.event',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                replaceSubGroupStr: '<!-- eventPlaceholder -->',
            },
            competition: {
                container: '.event-container',
                group: '.comp-container',
                subGroup: '.mg-wrapper, .btmarket__wrapper',
                row: '.event',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                replaceSubGroupStr: '<!-- eventPlaceholder -->',
            },
        };

        return config[type] || {};
    },

    /**
     * Abort Ajax request if needed
     *
     * @returns {void}
     *
     * @private
     */
    abort: function () {

        if (pagination.request && typeof pagination.request.abort === 'function') {

            pagination.request.abort();
        }
    },

    /**
     * Gets more data to Lazy Load
     *
     * @param {Number}   rowNumber
     * @param {String}   direction
     * @param {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getMoreData: function (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (pageNumber > 0 && pageNumber <= parseInt(window.WH.sportsbook.totalPages, 10)) {

            pagination.getPage(rowNumber, direction, callback);
        } else {

            callback({ totalRows: 0, compiled: [] }, direction, rowNumber);
        }
    },

    /**
     * Get data for selected page
     *
     * @param   {Number}   rowNumber
     * @param   {String}   direction
     * @param   {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getPage: function (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (direction === 1) {

            pagination.request = PageCommon.getJSONResource(
                pagination.baseLink + pageNumber + '.json',
                {
                    'afterSuccess': pagination.getPageSuccess.bind(this, callback, rowNumber),
                    'afterError': callback.bind(this, { totalRows: 0, compiled: [] }, direction),
                }
            );
        }
    },

    /**
     * Function to filter already existing data
     *
     * @param   {Object}   data
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     *
     * @returns {void}
     *
     * @private
     */
    filterPaginationData: function (data, rowNumber, direction) {

        var eventsToFilter = 0;

        if (direction === 1) {

            eventsToFilter = ((rowNumber - 1) % parseInt(window.WH.sportsbook.eventsPerPage, 10));
        }

        pagination.removeCompiledRows(
            data,
            eventsToFilter,
            direction
        );
    },

    /**
     * Remove compiled rows that we don't need
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    removeCompiledRows: function (data, eventsToFilter, direction) {

        /**
         * A set of helper functions
         *
         * @type {Object}
         */
        var helperFunctions = {
            /**
             * Remove groups
             *
             * @param   {Object} group
             *
             * @returns {Boolean}
             *
             * @private
             */
            groupsRemove: function (group) {

                group.rows = _.filter(group.rows, helperFunctions.subgroupsRemove);

                if (group.rows.length === 0) {

                    return false;
                }

                return true;
            },
            /**
             * Remove subgroups
             *
             * @param   {Object} subgroup
             *
             * @returns {Boolean}
             *
             * @private
             */
            subgroupsRemove: function (subgroup) {

                subgroup.rows = _.filter(subgroup.rows, helperFunctions.rowsRemove);

                if (subgroup.rows.length === 0) {

                    return false;
                }

                return true;
            },
            /**
             * Remove rows
             *
             * @param   {Object} row
             *
             * @returns {Boolean}
             *
             * @private
             */
            rowsRemove: function (row) {

                return !_.includes(rowsToRemove, row.key);
            },
        };

        if (eventsToFilter > 0) {

            pagination.rowIds = [];
            var rowsToRemove = pagination.rowsToRemove(data, eventsToFilter, direction);
            pagination.specialRows(data, rowsToRemove, direction);
            data.data.compiled = _.filter(data.data.compiled, helperFunctions.groupsRemove);
        }
    },

    /**
     * For given key, find it's group and subgroup
     *
     * @param   {String} key
     * @param   {Object} data
     *
     * @returns {Object}
     *
     * @private
     */
    findParents: function (key, data) {

        var parents = { 'group': null, 'subgroup': null };

        _.find(data.data.compiled, pagination.findGroup.bind(this, key, parents));

        return parents;
    },

    /**
     * Find given group
     *
     * @param   {String} key
     * @param   {Object} parents
     * @param   {Object} group
     *
     * @returns {Boolean}
     *
     * @private
     */
    findGroup: function (key, parents, group) {

        if (_.find(group.rows, pagination.findSubgroup.bind(this, key, parents))) {

            parents.group = group.key;
            return true;
        }

        return false;
    },

    /**
     * Find if given subgroup is in group
     *
     * @param   {String} key
     * @param   {Object} parents
     * @param   {Object} subgroup
     *
     * @returns {Boolean}
     *
     * @private
     */
    findSubgroup: function (key, parents, subgroup) {

        if (_.find(subgroup.rows, pagination.findRow.bind(this, key))) {

            parents.subgroup = subgroup.key;
            return true;
        }

        return false;
    },

    /**
     * Find if given row is in subgroup
     *
     * @param   {String} key
     * @param   {Object} row
     *
     * @returns {Boolean}
     *
     * @private
     */
    findRow: function (key, row) {

        return row.key === key;
    },

    /**
     * Special rows handling for partial pages
     *
     * @param   {Object} data
     * @param   {Array} rowsToRemove
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    specialRows: function (data, rowsToRemove, direction) {

        var eventIds = Object.keys(data.data.model.events);

        if (direction === 1) {
            pagination.removeExistingGroupTemplate(
                pagination.findParents(eventIds[0], data),
                pagination.findParents(rowsToRemove[rowsToRemove.length - 1], data),
                data.data.compiled
            );
        }
    },

    /**
     * Loop over subgroups
     *
     * @param   {Object} group
     *
     * @returns {void}
     *
     * @private
     */
    groupRows: function (group) {

        _.each(group.rows, pagination.subgroupRowIds);
    },

    /**
     * Loop over rows
     *
     * @param   {Object} subgroup
     *
     * @returns {void}
     *
     * @private
     */
    subgroupRowIds: function (subgroup) {

        pagination.rowIds = pagination
            .rowIds.concat(_.map(subgroup.rows, 'key'));
    },

    /**
     * Obtain row IDs given the compiled Array
     *
     * @param   {Array} compiled
     *
     * @returns {void}
     *
     * @private
     */
    obtainRowIds: function (compiled) {

        _.each(compiled, pagination.groupRows);
    },

    /**
     * Select rows to be removed
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {Array} an array of Row Ids
     *
     * @private
     */
    rowsToRemove: function (data, eventsToFilter, direction) {

        pagination.obtainRowIds(data.data.compiled);

        if (direction === 1) {
            pagination.rowIds = pagination.rowIds.splice(0, eventsToFilter);
        }

        _.each(pagination.rowIds, function (eventId) {

            delete data.data.model.events[eventId];
        });

        return pagination.rowIds;
    },

    /**
     * Do model updates
     *
     * @param   {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    doModelUpdates: function (model) {

        BasePage.ModelComposer.extendModel(model);
        pagination.updateSubscription(model);
    },

    /**
     * Function called after obtaining next page data to parse it and save it
     *
     * @param   {Function} callback
     * @param   {Number}   rowNumber
     * @param   {Object}   data
     *
     * @returns {void}
     *
     * @private
     */
    saveNextPage: function (callback, rowNumber, data) {

        pagination.filterPaginationData(data, rowNumber, 1);

        pagination.removeExistingGroupTemplate(
            pagination.pointers[pagination.pointers.length - 1].last,
            data.data.pointers.first,
            data.data.compiled
        );

        pagination.handlePointers(data.data.pointers);
        pagination.doModelUpdates(data.data.model);

        if (typeof callback === 'function') {

            callback({
                totalRows: _.size(data.data.model.markets),
                compiled: data.data.compiled,
            }, 1);
        }
    },

    /**
     * Function to handle a success response from fetching a page, but that does not contain any data.
     * This can occur when the server has an unhandled timeout for example.
     *
     * @param callback A function to call with the result of saving: nr of rows and compiled data
     *
     * @returns {void}
     *
     * @private
     */
    handleEmptyPageData: function (callback) {

        if (typeof callback === 'function') {

            callback({
                totalRows: 0,
                compiled: [],
            }, 1);
        }
    },

    /**
     * Function called after a new page was successfully fetched. It handles the scenario where we have a
     * success response but might be empty, due to timeouts on the server for example.
     *
     * @param {Function} callback A function to call with the result of saving: nr of rows and compiled data
     * @param {integer} rowNumber The row number this page starts from
     * @param {Object} data Fetched from the server representing the page (model, pointers, compiled ...)
     *
     * @returns {void}
     *
     * @private
     */
    getPageSuccess: function (callback, rowNumber, data) {

        if (!_.isEmpty(data) && !_.isEmpty(data.data) &&
            !_.isEmpty(data.data.pointers)) {

            pagination.saveNextPage(callback, rowNumber, data);
        } else {

            pagination.handleEmptyPageData(callback);
        }
    },

    /**
     * Removes existing subgroup template if present on current page
     *
     * @param   {Object} currentPointers
     * @param   {Object} newPointers
     * @param   {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingSubgroupTemplate: function (currentPointers, newPointers, data) {

        var group = _.find(data, { 'key': newPointers.group });

        if (currentPointers.subgroup === newPointers.subgroup &&
            _.find(group.rows, { 'key': newPointers.subgroup })) {

            delete _.find(group.rows, { 'key': newPointers.subgroup }).template;
        }
    },

    /**
     * Removes existing group template if present on current page
     *
     * @param   {Object} currentPointers
     * @param   {Object} newPointers
     * @param   {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingGroupTemplate: function (currentPointers, newPointers, data) {

        if (currentPointers.group === newPointers.group &&
            _.find(data, { 'key': newPointers.group })) {

            delete _.find(data, { 'key': newPointers.group }).template;
            pagination.removeExistingSubgroupTemplate(currentPointers, newPointers, data);
        }
    },

    /**
     * Initializes pagination
     *
     * @returns {void}
     *
     * @private
     */
    initializePagination: function () {

        pagination.request = null;
        pagination.pointers = [];
        pagination.baseLink = window.WH.sportsbook.baseLink;
        pagination.handlePointers(window.WH.sportsbook.currentPointers);
    },

    /**
     * This functions handles removing events from model
     *
     * @param   {Array} eventsToRemove
     *
     * @returns {void}
     *
     * @private
     */
    updateModel: function (eventsToRemove) {

        BasePage.ModelComposer.removeEventsFromModel(eventsToRemove);
    },

    /**
     * Subscribed to given events and their descendands
     *
     * @param   {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    updateSubscription: function (model) {

        _.each(
            model,
            subscribeSetToPush
        );

        _.each(
            model.events,
            subscribeToLivescore
        );
    },

    /**
     * Add pointers
     *
     * @param   {Object} newPointers
     *
     * @returns {void}
     *
     * @private
     */
    handlePointers: function (newPointers) {

        if (pagination.pointers.length >= 5) {
            pagination.pointers = pagination.pointers.slice(1, 5);
        }

        pagination.pointers.push(newPointers);
    },
};

/**
 * Function used to initialize views on the page
 *
 * @returns {void}
 *
 * @private
 */
var initViews = function () {

    BasePage.setOddsFormat();

    SelectionViewAdapter.init(
        deps.sportsDataRepository.getModel().selections, content
    );

};

/**
 * Init the model
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var initModel = function (regenerateModel) {

    if (regenerateModel === true) {

        BasePage.ModelComposer.regenerateMergedModel();
        unsubscribePush();
        LivescoreDiffusionAdapter.unsubscribeAll();
    }

    subscribeModel(deps.sportsDataRepository.getModel());
};

/**
 * Function unsubscribes all from DomPushAdapter and PushAdapter
 *
 * @returns {void}
 *
 * @private
 */
var unsubscribePush = function () {

    PageCommon.DomPushAdapter.unsubscribeAll();
    PageCommon.PushAdapter.unsubscribeAll();
};

/**
 * Subscribes model entities to push events and model events to livescore
 *
 * @param   {Object} model Sportsbook Model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeModel = function (model) {

    _.each(
        model,
        subscribeSetToPush
    );

    _.each(
        model.events,
        subscribeToLivescore
    );
};

/**
 * Performs initializations
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var performInit = function (regenerateModel) {

    initModel(regenerateModel);
    initViews();
};

/**
 * Subscribe a set of entities (events, markets, etc) to PUSH updates
 *
 * @param   {Object} set An object containing entities, such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set) {

    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param   {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function (entity) {

    if (entity && entity.id && !entity.id.match(/OB_TY/g) &&
        PageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

        PageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};

/**
 * Subscribe an in play event to livescore updates
 *
 * @param   {Object} event   Event model
 * @param   {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToLivescore = function (event, eventId) {

    if (event.isInPlay) {

        LivescoreDiffusionAdapter.subscribe(
            BasePage.getSportEnSlug(event),
            eventId,
            EventViewAdapter.livescore(eventId, content).update
        );
    }
};

/**
 * Function used to display the livescore for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showLivescore = function ($event, eventId) {

    $event.find('.area-livescore time').addClass('displayNone');
    $event.find('.area-livescore .event__status').removeClass('displayNone');
    LivescoreDiffusionAdapter.subscribe(
        BasePage.getSportEnSlug($event),
        eventId,
        EventViewAdapter.livescore(eventId, content).update
    );
};

/**
 * Function used to display the start time for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showStartTime = function ($event, eventId) {

    $event.find('.area-livescore time').removeClass('displayNone');
    $event.find('.area-livescore .event__status').addClass('displayNone');
    LivescoreDiffusionAdapter.unsubscribe(eventId);
};

/**
 * Show livescore or start time, based on isInPlay status
 * received from a PUSH message
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var handleLivescore = function ($event, message) {

    if (message.value === true) {

        showLivescore($event, message.id);
    } else {

        showStartTime($event, message.id);
    }
};

/**
 * Function sets Page internal args
 *
 * @param {Object} content jQuery object containing the page content
 * @param {Object} deps Page dependencies
 * @param {Object} args arguments like sport or locale, etc...
 *
 * @returns {void}
 *
 * @private
 */
var setInternalArgs = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;
};

/**
 * This function checks if the entity was the last one displayed in order to hide containers
 * or to unhide containers if the entity is about to be displayed
 *
 * @param {Object}  $event jQuery element
 * @param {Boolean} hide Hide flag
 *
 * @returns {void}
 *
 * @private
 */
var manageEventContainers = function ($event, hide) {

    // A hierarchical parent containers selectors array going from event level up
    var eventContainersSelectors = [
        '.events-group-container',
        '.markets-group-container',
        '.coupon-container',
    ];

    if (hide) {

        hideEventContainers($event, '.event', eventContainersSelectors);
    } else {

        showEventContainers($event, '.event', eventContainersSelectors);
    }
};

/**
 * Function propagates event parent containers hide action within given selectors Array
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var hideEventContainers = function ($entity, entitySelector, selectors) {

    if (selectors.length > 0) {

        var parentSelector = selectors.shift();
        hideParentContainer($entity, entitySelector, parentSelector, selectors);
    }
};

/**
 * Function checks and hides parent container, if all of the same level elements are already hidden.
 * And calls back hideEventContainers function to process with the rest of selectors
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {String} parentSelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var hideParentContainer = function ($entity, entitySelector, parentSelector, selectors) {

    // If all Entity siblings are hidden, hide parent container
    if (!hasVisibleSiblings($entity, entitySelector)) {

        var $parentContainer = $entity.parents(parentSelector);
        $parentContainer.addClass('displayNone');
        handleCouponsMenu($parentContainer, parentSelector);
        hideEventContainers($parentContainer, parentSelector, selectors);
    }
};

/**
 * Function propagates event parent containers unhide action within given selectors Array
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var showEventContainers = function ($entity, entitySelector, selectors) {

    var parentSelector;

    if (selectors.length > 0) {

        parentSelector = selectors.shift();
        showParentContainer($entity, entitySelector, parentSelector, selectors);
    }

};

/**
 * Function checks and unhides parent container, if entity is displayed but parent container is hidden.
 * And calls back showEventContainers function to process with the rest of selectors
 *
 * @param {Object} $entity jQuery element (current entity in the loop)
 * @param {String} entitySelector jQuery element selector
 * @param {String} parentSelector jQuery element selector
 * @param {Array} selectors Array of jQuery selectors for event containers
 *
 * @returns {void}
 *
 * @private
 */
var showParentContainer = function ($entity, entitySelector, parentSelector, selectors) {

    var $parentContainer = $entity.parents(parentSelector);

    if (!$entity.hasClass('displayNone') && $parentContainer.hasClass('displayNone')) {

        $parentContainer.removeClass('displayNone');
        handleCouponsMenu($parentContainer, parentSelector);
        showEventContainers($parentContainer, parentSelector, selectors);
    }
};

/**
 * Checks if the element has at least one visible siblings. Breaks on first match
 *
 * @param {jQuery} $element Element to check
 * @param {String} [selector] Optional selector for the siblings
 *
 * @returns {boolean} true if the element has visible siblings
 *
 * @private
 */
var hasVisibleSiblings = function ($element, selector) {

    var siblings = $element.siblings(selector);

    return _.some(siblings, isDisplayedElement);
};

/**
 * Checks if the provided element *does not* have the class 'displayNone'
 *
 * @param {Object} element
 *
 * @returns {boolean} true if it has class displayNone
 *
 * @private
 */
var isDisplayedElement = function (element) {

    return !element.classList.contains('displayNone');
};

/**
 * Checks if it's Coupon Container we are applying changes to and calls menu item visibility toggle
 * to show or hide depending on coupon container's visibility
 *
 * @param {Object} $couponContainer jQuery element
 * @param {String} selector string selector for coupon container
 *
 * @returns {void}
 *
 * @private
 */
var handleCouponsMenu = function ($couponContainer, selector) {

    if (selector === '.coupon-container') {

        toggleMenuCouponVisibility(
            $couponContainer.attr('data-coupon-id'),
            $couponContainer.hasClass('displayNone')
        );
    }
};

/**
 * Toggles coupons menu item visibility
 *
 * @param {String} couponId coupon id string value
 * @param {Boolean} show boolean to show or hide menu item
 *
 * @returns {void}
 *
 * @private
 */
var toggleMenuCouponVisibility = function (couponId, hide) {

    var menuLink = jQuery('#coupons-nav').find('a[data-coupon-id="' + couponId + '"]');
    menuLink.parent('section.marketmenu__section').toggleClass('displayNone', hide);
};

/**
 * Push related functionality
 *
 * @type {Object}
 *
 * @private
 */
var push = {

    /**
     * DOM PUSH update callback function
     * This function is called every time a push  message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @returns {void}
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {

            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     *
     * @private
     */
    handlers: {
        'displayed': 'displayedUpdateCase',
        'betInRun': 'betInRunUpdateCase',
    },

    /**
     * Getting the first market for an event present in the model
     *
     * @param {Object} event A model event
     *
     * @returns {Object} The market for the given event
     *
     * @private
     */
    getEventFirstMarket: function (event) {

        if (event && window.WH.sportsbook.model.markets && !_.isEmpty(event.markets)) {

            return window.WH.sportsbook.model.markets[event.markets[0]];
        } else {

            return null;
        }
    },

    /**
     * Before toggling an event to visible we have to check in play status
     *
     * @param {String} eventId The id of the event
     *
     * @returns {Boolean} indicates if the event should be toggled to visible or hidden
     *
     * @private
     */
    checkInPlayStatus: function (eventId) {

        var event = window.WH.sportsbook.model.events[eventId];
        var market = push.getEventFirstMarket(event);

        return market && (event.isInPlay === market.hasInPlay);
    },

    /**
     * Update event visibility on bet in run message
     *
     * @param {Object} $event jQuery element
     * @param {Object} message DOM PUSH message
     *
     * @returns {void}
     *
     * @private
     */
    betInRunUpdateDisplay: function ($event, message) {

        if (window.WH.sportsbook.model.events[message.id]) {

            var eventDisplayed = window.WH.sportsbook.model.events[message.id].displayed.toString() === 'true';
            var show = eventDisplayed && push.checkInPlayStatus(message.id);

            // Hide pre-match event if value === true, as it's turning to in-play
            $event.toggleClass('displayNone', !show);
            manageEventContainers($event, !show);
        }
    },

    /**
     * Handles the dom push message containing an display attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @returns {void}
     *
     * @private
     */
    displayedUpdateCase: function (message) {

        if (message.nodeType === 'events') {

            var $event = jQuery('#' + message.id);

            var show = message.value && push.checkInPlayStatus(message.id);
            manageEventContainers($event, !show);
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     *
     * @param  {Object} message DOM PUSH message
     *
     * @returns {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        if (message.nodeType === 'events') {

            BasePage.propagateTopBetsBetInRun(message);

            var $event = jQuery('#' + message.id);

            $event.attr('data-betinrun', message.value);

            push.betInRunUpdateDisplay($event, message);

            handleLivescore($event, message);
        }
    },
};

/**
 * Contains functions exposed by this component as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page based on the args
     *
     * @param {Object} args Page arguments
     *
     * @returns {String} Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        var contentURL = '/betting/' + args.locale + '/' + args.sport + '/coupons' + '/' + args.sortOrder;

        //the coupon id can be undefined , but not the sort order, cause we have a default value for that.
        if (_.isUndefined(args.couponId)) {

            return contentURL;
        } else {

            return contentURL + '/' + args.couponId;
        }
    },

    /**
     * Function called after the DOM ready event is fired
     *
     * @param {Object} deps Object that contains page dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(jQuery, 'coupons', args.sport);

        if (deps.ready) {
            deps.ready('coupons');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function () {

        BasePage.switchTab('Coupons');
        performInit(false);
        pagination.init();
    },

    // bindNavigation: bindNavigation,

    bindNavigation(content, deps, args) {

        setInternalArgs(content, deps, args);
        MarketMenuAdapter.init();
    },

    /**
     * Changes rendered state flag to true
     *
     * @returns {void}
     *
     * @public
     */
    setRendered: function () {

        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @returns {void}
     *
     * @public
     */
    setUnrendered: function () {

        content = null;
        deps = null;
        args = null;

        BasePage.LazyLoadingViewAdapter.destroy();
        BasePage.setObjectValues(pagination, {
            baseLink: null,
            pointers: [],
            currentPointers: null,
            request: null,
            rowIds: [],
        });
        rendered = false;
    },

    /**
     * Returns the value of the internal rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'coupons',
};

export default api;
